import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useLang } from "@/context/LangContext";

const mapsLink = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const telLink = (p) => `tel:${(p || "").replace(/[^+0-9]/g, "")}`;

export default function Contact({ content }) {
  const { t } = useLang();
  if (!content) return null;

  return (
    <section
      id="contact"
      className="afb-section bg-afb-ink text-white"
      data-testid="contact-section"
    >
      <div className="afb-container">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="afb-eyebrow !text-afb-gold">{t("sections.contact_label")}</span>
          <h2 className="serif text-[1.7rem] sm:text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.15]">
            {t("sections.contact_subtitle")}
          </h2>
          <span className="block mt-5 h-[2px] w-16 sm:w-20 bg-[color:var(--afb-gold)]" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
          <Block icon={<MapPin className="w-5 h-5" />} title={t("sections.offices")}>
            <a
              href={mapsLink(content.address_melendugno)}
              target="_blank"
              rel="noreferrer"
              className="block hover:text-afb-gold transition-colors"
              data-testid="contact-addr-melendugno"
            >
              {content.address_melendugno}
            </a>
            <a
              href={mapsLink(content.address_borgagne)}
              target="_blank"
              rel="noreferrer"
              className="block mt-2 hover:text-afb-gold transition-colors"
              data-testid="contact-addr-borgagne"
            >
              {content.address_borgagne}
            </a>
          </Block>

          <Block icon={<Phone className="w-5 h-5" />} title={t("sections.phones")}>
            <a
              href={telLink(content.phone_giuseppe)}
              className="block hover:text-afb-gold transition-colors"
              data-testid="contact-phone-giuseppe"
            >
              <span className="text-white/55 text-xs uppercase tracking-widest mr-2">
                Giuseppe
              </span>
              {content.phone_giuseppe}
            </a>
            <a
              href={telLink(content.phone_daniele)}
              className="block mt-2 hover:text-afb-gold transition-colors"
              data-testid="contact-phone-daniele"
            >
              <span className="text-white/55 text-xs uppercase tracking-widest mr-2">
                Daniele
              </span>
              {content.phone_daniele}
            </a>
            <a
              href={telLink(content.phone_fixed)}
              className="block mt-2 hover:text-afb-gold transition-colors"
              data-testid="contact-phone-fixed"
            >
              <span className="text-white/55 text-xs uppercase tracking-widest mr-2">
                Fisso
              </span>
              {content.phone_fixed}
            </a>
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-afb-gold">
              <Clock className="w-3.5 h-3.5" />
              {t("common.available_24_7")}
            </div>
          </Block>

          <Block icon={<Mail className="w-5 h-5" />} title={t("sections.email")}>
            <a
              href={`mailto:${content.email}`}
              className="break-all hover:text-afb-gold transition-colors"
              data-testid="contact-email"
            >
              {content.email}
            </a>
          </Block>
        </div>
      </div>
    </section>
  );
}

function Block({ icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-full border border-afb-gold/40 flex items-center justify-center text-afb-gold">
          {icon}
        </span>
        <span className="text-xs uppercase tracking-[0.28em] text-white/60">{title}</span>
      </div>
      <div className="text-white/90 text-sm sm:text-base leading-relaxed">{children}</div>
    </div>
  );
}
